import {
  AlertCircle,
  CheckCircle2,
  Hospital,
  IdCard,
  Search,
  Stethoscope,
} from "lucide-react";
import { type FormEvent, useCallback, useMemo, useState } from "react";
import { lookupPrivilege } from "../lib/api";
import { formatDateTime, formatThaiDate } from "../lib/format";
import type { LookupResult, LookupType } from "../shared/types";
import { Turnstile } from "./Turnstile";

const modes: Array<{
  type: LookupType;
  label: string;
  hint: string;
  icon: typeof Hospital;
}> = [
  { type: "hn", label: "HN", hint: "Hospital Number", icon: Hospital },
  { type: "national_id", label: "เลขบัตรประชาชน", hint: "เลข 13 หลัก", icon: IdCard },
];

export function PublicLookup() {
  const [mode, setMode] = useState<LookupType>("hn");
  const [value, setValue] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const selected = useMemo(() => modes.find((item) => item.type === mode) ?? modes[0], [mode]);
  const Icon = selected.icon;
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(""), []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await lookupPrivilege({
        type: mode,
        value,
        turnstileToken,
      });
      setResult(response.result ?? null);
    } catch (lookupError) {
      setError((lookupError as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className={result || error ? "lookup-page has-feedback" : "lookup-page"}>
      <div className="lookup-intro">
        <p className="eyebrow">ตรวจสอบแบบตรงตัวเท่านั้น</p>
        <h1>ตรวจสิทธิผู้บริจาค</h1>
        <p>
          สำหรับเจ้าหน้าที่หน้างาน เช่น จุดจอดรถหรือจุดบริการผู้ป่วย ระบบจะแสดงเฉพาะสถานะสิทธิ
          ไม่แสดงข้อมูลส่วนตัวที่ไม่จำเป็น
        </p>
      </div>

      <form className="lookup-panel" onSubmit={handleSubmit}>
        <div className="mode-grid" role="tablist" aria-label="วิธีค้นหา">
          {modes.map((item) => {
            const ModeIcon = item.icon;
            return (
              <button
                className={item.type === mode ? "mode-button active" : "mode-button"}
                key={item.type}
                type="button"
                onClick={() => {
                  setMode(item.type);
                  setValue("");
                  setResult(null);
                  setError("");
                }}
              >
                <ModeIcon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="lookup-fields">
          <label className="lookup-input">
            <span>
              <Icon size={18} />
              {selected.label}
            </span>
            <input
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={selected.hint}
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
        </div>

        <Turnstile
          siteKey={siteKey}
          onToken={setTurnstileToken}
          onExpire={handleTurnstileExpire}
        />

        <button className="primary-action" type="submit" disabled={loading}>
          <Search size={18} />
          {loading ? "กำลังตรวจสอบ" : "ตรวจสอบสิทธิ"}
        </button>
      </form>

      {error ? (
        <div className="status-card warning" role="alert">
          <AlertCircle size={22} />
          <div>
            <strong>ไม่สามารถยืนยันสิทธิได้</strong>
            <p>{error}</p>
          </div>
        </div>
      ) : null}

      {result ? <LookupResultCard result={result} /> : null}
    </section>
  );
}

function LookupResultCard({ result }: { result: LookupResult }) {
  return (
    <article className={result.active ? "result-card active" : "result-card inactive"}>
      <header>
        {result.active ? <CheckCircle2 size={28} /> : <AlertCircle size={28} />}
        <div>
          <p>{result.active ? "พบสิทธิที่ใช้งานได้" : "พบข้อมูล แต่สิทธิไม่พร้อมใช้งาน"}</p>
          <h2>{result.privilegeLevel}</h2>
        </div>
      </header>

      <div className="result-grid">
        <div>
          <span>ประเภทสิทธิ</span>
          <strong>{result.roleLabel}</strong>
        </div>
        <div>
          <span>ที่จอดรถ</span>
          <strong>{result.parking.eligible ? "มีสิทธิ" : "ไม่มีสิทธิ"}</strong>
          <small>{result.parking.reason}</small>
        </div>
        <div>
          <span>ค่าห้องพิเศษ &lt; 3,000 บาท</span>
          <strong>{result.roomDiscount.under3000}</strong>
        </div>
        <div>
          <span>ค่าห้องพิเศษ ≥ 3,000 บาท</span>
          <strong>{result.roomDiscount.over3000}</strong>
        </div>
        <div>
          <span>ระยะเวลาสิทธิ</span>
          <strong>{result.benefit.label}</strong>
          <small>สิ้นสุด: {formatThaiDate(result.benefit.validUntil)}</small>
        </div>
        <div className="pmc-result">
          <span className="result-label-with-icon"><Stethoscope size={16} /> บริการห้อง PMC</span>
          <strong>{result.pmc.eligible ? "มีสิทธิ" : "ไม่มีสิทธิ"}</strong>
          <small>{result.pmc.reason}</small>
          {result.pmc.eligible ? (
            <div className="pmc-result-details">
              <span>
                <small>จำนวนคงเหลือปีนี้</small>
                <b>
                  {result.pmc.remainingUses === null
                    ? "ไม่จำกัด"
                    : `${result.pmc.remainingUses} / ${result.pmc.annualLimit} ครั้ง`}
                </b>
              </span>
              <span>
                <small>ใช้สิทธิล่าสุด</small>
                <b>{result.pmc.latestUsedAt ? formatThaiDate(result.pmc.latestUsedAt) : "ยังไม่เคยใช้"}</b>
              </span>
            </div>
          ) : null}
        </div>
        <div>
          <span>เวลาตรวจสอบ</span>
          <strong>{formatDateTime(result.checkedAt)}</strong>
        </div>
      </div>
    </article>
  );
}
